import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  Paper,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { deviceAPI } from '../services/api';
import { DeviceTypeOptions, CreateDeviceRequest } from '../types';

interface BulkImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (count: number) => void;
}

interface DeviceImportRow {
  imei: string;
  plateNumber: string;
  make: string;
  model: string;
  modelYear: string;
  color?: string;
  deviceType: DeviceTypeOptions;
  status: 'valid' | 'invalid' | 'duplicate';
  errors: string[];
}

const BulkImportDialog: React.FC<BulkImportDialogProps> = ({
  open,
  onClose,
  onImported
}) => {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'complete'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [deviceData, setDeviceData] = useState<DeviceImportRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResults, setImportResults] = useState<{
    successful: number;
    failed: number;
    errors: string[];
  }>({ successful: 0, failed: 0, errors: [] });

  const validDeviceTypes: DeviceTypeOptions[] = ['My Car Pro', 'My Car Lite', 'OBD2', 'Hardwired'];

  const isValidDeviceType = (type: string): type is DeviceTypeOptions => {
    return validDeviceTypes.includes(type as DeviceTypeOptions);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      parseCSVFile(uploadedFile);
    }
  };

  const parseCSVFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target?.result as string;
      const lines = csv.split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      
      const devices: DeviceImportRow[] = [];
      
      for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim() === '') continue;
        
        const values = lines[i].split(',').map(v => v.trim());
        const deviceTypeValue = (values[6] || 'My Car Pro').trim();
        
        const device: DeviceImportRow = {
          imei: values[0] || '',
          plateNumber: values[1] || '',
          make: values[2] || '',
          model: values[3] || '',
          modelYear: values[4] || '',
          color: values[5] || '',
          deviceType: isValidDeviceType(deviceTypeValue) ? deviceTypeValue : 'My Car Pro',
          status: 'valid',
          errors: []
        };
        
        // Validation
        if (!device.imei || device.imei.length !== 15) {
          device.status = 'invalid';
          device.errors.push('Invalid IMEI (must be 15 digits)');
        }
        
        if (!device.plateNumber) {
          device.status = 'invalid';
          device.errors.push('Plate number is required');
        }
        
        if (!device.make) {
          device.status = 'invalid';
          device.errors.push('Make is required');
        }
        
        if (!device.model) {
          device.status = 'invalid';
          device.errors.push('Model is required');
        }
        
        const year = parseInt(device.modelYear);
        if (!year || year < 1900 || year > new Date().getFullYear() + 1) {
          device.status = 'invalid';
          device.errors.push('Invalid model year');
        }

        // Validate device type
        if (!isValidDeviceType(deviceTypeValue)) {
          device.errors.push(`Invalid device type. Must be one of: ${validDeviceTypes.join(', ')}`);
          device.status = 'invalid';
        }
        
        devices.push(device);
      }
      
      setDeviceData(devices);
      setStep('preview');
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    setStep('importing');
    setImporting(true);
    setImportProgress(0);
    
    const validDevices = deviceData.filter(d => d.status === 'valid');
    let successful = 0;
    let failed = 0;
    const errors: string[] = [];
    
    for (let i = 0; i < validDevices.length; i++) {
      try {
        const device = validDevices[i];
        const createDeviceRequest: CreateDeviceRequest = {
          imei: device.imei,
          plateNumber: device.plateNumber,
          make: device.make,
          model: device.model,
          modelYear: device.modelYear,
          color: device.color,
          deviceType: device.deviceType,
          status: 'offline',
          isActive: true
        };
        
        await deviceAPI.createDevice(createDeviceRequest);
        successful++;
      } catch (error: any) {
        failed++;
        errors.push(`Row ${i + 2}: ${error.response?.data?.message || error.message}`);
      }
      
      setImportProgress(((i + 1) / validDevices.length) * 100);
    }
    
    setImportResults({ successful, failed, errors });
    setStep('complete');
    setImporting(false);
    
    if (successful > 0) {
      onImported(successful);
    }
  };

  const downloadTemplate = () => {
    const template = `imei,plateNumber,make,model,modelYear,color,deviceType
123456789012345,ABC123,Toyota,Camry,2023,White,My Car Pro
234567890123456,DEF456,Honda,Civic,2022,Black,My Car Lite
345678901234567,GHI789,Ford,Focus,2021,Blue,OBD2`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'device_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setDeviceData([]);
    setImportResults({ successful: 0, failed: 0, errors: [] });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Bulk Import Devices
        {step === 'importing' && (
          <LinearProgress variant="determinate" value={importProgress} sx={{ mt: 1 }} />
        )}
      </DialogTitle>
      
      <DialogContent>
        {step === 'upload' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Import Multiple Devices from CSV
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
              Upload a CSV file with device information. Make sure your CSV includes:
              IMEI, Plate Number, Make, Model, Model Year, Color (optional), Device Type
            </Alert>
            
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ mb: 2 }}
              >
                Download CSV Template
              </Button>
            </Box>
            
            <Button
              variant="contained"
              component="label"
              startIcon={<UploadIcon />}
              size="large"
            >
              Choose CSV File
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            
            {file && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Selected: {file.name}
              </Typography>
            )}
          </Box>
        )}
        
        {step === 'preview' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Preview Import Data ({deviceData.length} devices)
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`Valid: ${deviceData.filter(d => d.status === 'valid').length}`} 
                color="success" 
                sx={{ mr: 1 }} 
              />
              <Chip 
                label={`Invalid: ${deviceData.filter(d => d.status === 'invalid').length}`} 
                color="error" 
              />
            </Box>
            
            <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>IMEI</TableCell>
                    <TableCell>Plate Number</TableCell>
                    <TableCell>Make</TableCell>
                    <TableCell>Model</TableCell>
                    <TableCell>Year</TableCell>
                    <TableCell>Device Type</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deviceData.map((device, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {device.status === 'valid' ? (
                          <CheckIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </TableCell>
                      <TableCell>{device.imei}</TableCell>
                      <TableCell>{device.plateNumber}</TableCell>
                      <TableCell>{device.make}</TableCell>
                      <TableCell>{device.model}</TableCell>
                      <TableCell>{device.modelYear}</TableCell>
                      <TableCell>{device.deviceType}</TableCell>
                      <TableCell>
                        {device.errors.length > 0 && (
                          <Typography variant="caption" color="error">
                            {device.errors.join(', ')}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Box>
        )}
        
        {step === 'importing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              Importing Devices...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {Math.round(importProgress)}% complete
            </Typography>
          </Box>
        )}
        
        {step === 'complete' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Import Complete
            </Typography>
            
            <Alert severity={importResults.failed > 0 ? "warning" : "success"} sx={{ mb: 2 }}>
              Successfully imported {importResults.successful} devices.
              {importResults.failed > 0 && ` ${importResults.failed} devices failed.`}
            </Alert>
            
            {importResults.errors.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Import Errors:
                </Typography>
                <Paper sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
                  {importResults.errors.map((error, index) => (
                    <Typography key={index} variant="caption" display="block" color="error">
                      {error}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          {step === 'complete' ? 'Close' : 'Cancel'}
        </Button>
        
        {step === 'upload' && file && (
          <Button onClick={() => parseCSVFile(file)} variant="outlined">
            Parse File
          </Button>
        )}
        
        {step === 'preview' && (
          <Button 
            onClick={handleBulkImport}
            variant="contained"
            disabled={deviceData.filter(d => d.status === 'valid').length === 0}
          >
            Import {deviceData.filter(d => d.status === 'valid').length} Devices
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkImportDialog;